import { Midi } from '@tonejs/midi';
import { MODE_ALIASES } from './musicTheory/modes';

const NUMBER_KEYS = new Set(['tempo', 'bars']);

// @tonejs/midi decode le texte des meta-events en latin-1 (1 octet = 1 char) ;
// nos marqueurs sont ecrits en UTF-8 (♭ etc.), on re-decode quand la chaine
// ressemble a des octets UTF-8 bruts.
function decodeMarkerText(text) {
  if (!/[\u0080-\u00ff]/.test(text) || /[\u0100-\uffff]/.test(text)) return text;
  try {
    // eslint-disable-next-line no-restricted-globals
    return decodeURIComponent(escape(text));
  } catch {
    return text;
  }
}

function parseMetaString(metaStr) {
  const meta = {};
  const pairs = metaStr.split(',');
  for (const pair of pairs) {
    const eqIdx = pair.indexOf('=');
    if (eqIdx === -1) continue;
    const key = pair.slice(0, eqIdx).trim();
    const val = pair.slice(eqIdx + 1).trim();
    meta[key] = NUMBER_KEYS.has(key) ? Number(val) : val;
  }
  return meta;
}

function parseMarkerText(text) {
  const pipeIdx = text.indexOf('|');
  if (pipeIdx === -1) return { name: text.trim(), meta: {} };
  const meta = parseMetaString(text.slice(pipeIdx + 1));
  if (meta.scale) meta.scale = MODE_ALIASES[meta.scale] || meta.scale;
  return {
    name: text.slice(0, pipeIdx).trim(),
    meta
  };
}

export function parseMidiMarkers(midiArrayBuffer) {
  const midi = new Midi(midiArrayBuffer);
  const rawMarkers = midi.header.meta
    .filter(m => m.type === 'marker')
    .sort((a, b) => a.ticks - b.ticks);

  if (rawMarkers.length === 0) return [];

  const tickToSec = (ticks) => midi.header.ticksToSeconds(ticks);
  const totalDuration = midi.duration;

  const sectionMarkers = [];
  let unnamed = 0;
  for (const m of rawMarkers) {
    const text = decodeMarkerText(m.text).trim();
    if (text.startsWith('@')) continue;
    if (text === '') {
      unnamed++;
      sectionMarkers.push({ ...m, parsedName: `Section ${unnamed}` });
    } else {
      sectionMarkers.push({ ...m, parsedName: text });
    }
  }

  const processed = [];
  for (let i = 0; i < sectionMarkers.length; i++) {
    const m = sectionMarkers[i];
    const { name, meta } = parseMarkerText(m.parsedName);
    const startTime = tickToSec(m.ticks);
    const endTime = (i + 1 < sectionMarkers.length) ? tickToSec(sectionMarkers[i + 1].ticks) : totalDuration;
    processed.push({ name, startTime, endTime, isRange: false, meta });
  }

  return processed;
}

export function parseMidiFile(midiArrayBuffer) {
  const midi = new Midi(midiArrayBuffer);

  const ALL_NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  const noteEvents = [];
  for (const track of midi.tracks) {
    for (const note of track.notes) {
      noteEvents.push({
        time: note.time,
        note: ALL_NOTES[note.midi % 12]
      });
    }
  }
  noteEvents.sort((a, b) => a.time - b.time);
  const deduped = [];
  let lastTime = -1;
  for (const evt of noteEvents) {
    if (evt.time !== lastTime) {
      deduped.push(evt);
      lastTime = evt.time;
    }
  }

  const rawMarkers = midi.header.meta
    .filter(m => m.type === 'marker')
    .sort((a, b) => a.ticks - b.ticks);

  const tickToSec = (ticks) => midi.header.ticksToSeconds(ticks);
  const totalDuration = midi.duration;
  // Duree musicale exacte = EOT (fin de la derniere mesure), pas la derniere note.
  // Sert de point de bouclage sample-accurate au player (loop du morceau entier).
  const eotTicks = Math.max(0, ...midi.tracks.map(t => t.endOfTrackTicks || 0));
  const musicalDuration = eotTicks > 0 ? tickToSec(eotTicks) : totalDuration;

  const sectionMarkers = [];
  let unnamed = 0;
  for (const m of rawMarkers) {
    const text = decodeMarkerText(m.text).trim();
    if (text.startsWith('@')) continue;
    if (text === '') {
      unnamed++;
      sectionMarkers.push({ ...m, parsedName: `Section ${unnamed}` });
    } else {
      sectionMarkers.push({ ...m, parsedName: text });
    }
  }

  const sections = [];
  for (let i = 0; i < sectionMarkers.length; i++) {
    const m = sectionMarkers[i];
    const { name, meta } = parseMarkerText(m.parsedName);
    const startTime = tickToSec(m.ticks);
    const endTime = (i + 1 < sectionMarkers.length) ? tickToSec(sectionMarkers[i + 1].ticks) : totalDuration;
    sections.push({ name, startTime, endTime, isRange: false, meta });
  }

  return { noteEvents: deduped, sections, musicalDuration };
}
